import React, { useEffect, useState } from "react";
import I18n from "../../../../i18n/utils";
import "./DeliveryPartnerForm.scss";
import {
  Card,
  Button,
  Col,
  Row,
  InputGroup,
  FormGroup,
  FormFeedback,
  Label,
  Input,
  CardBody,
  CardFooter,
} from "reactstrap";
import CFASpinner from "../../Common/CFASpinnner/CFASpinner";
import IDeliveryPartnersForm from "../../../interfaces/IDeliveryPartnersForm";
import closeOutline from "../../../assets/icons/close-outline.svg";
import deliveryCar from "../../../assets/icons/delivery-car.svg";
import successIcon from "../../../assets/icons/success-icon.svg";
import DeliveryPartner from "../../../interfaces/DeliveryPartner";
import { getEndDay } from "../../../utils/DateUtil";
import { INPUT_VALIDATION_TYPES_DP } from "../../../utils/constants";
import { isInvalidForm } from "../../../utils/validation/validation";
import {
  deliveryPartnerBusinessUnitUpdateSelector,
  updateDeliveryPartners,
} from "../../../redux/features/deliveryPartnerSlice";
import { useDispatch, useSelector } from "react-redux";
import { getOktaUser } from "../../../utils/LocalStorage";

const DeliveryPartnersForm: React.FC<IDeliveryPartnersForm> = ({
  deliveryPartners,
  selectedPartner,
  setDeliveryPartnerForm,
  openCommissionRateForm,
  closeDeliveryPartnerModal,
}: IDeliveryPartnersForm): JSX.Element => {
  const [deliveryPartnerForm, setForm] = useState<DeliveryPartner>({
    // @ts-ignore
    deliveryPartnerId: "",
    deliveryPartnerName: "",
    // @ts-ignore
    grossSalesTransactionCode: "",
    // @ts-ignore
    commissionTransactionCode: "",
    salesWeekStartDay: "Monday",
    salesWeekEndDay: "Friday",
    paymentDayOfWeek: "Friday",
    lastUpdatedBy: "",
    createdBy: "",
  });

  const [formTitle, setFormTitle] = useState("");
  const [editingPartner, setEditingPartner] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const businessUnitOverridesForPartner = useSelector(
    deliveryPartnerBusinessUnitUpdateSelector
  );
  const oktaUserInfo = getOktaUser();

  useEffect(() => {
    let arrayPos, deliveryPartnerName, _editingPartner;
    // array position of delivery partner to be edited
    arrayPos = getArrayPos();

    //Determine if editing existing partner or adding a new partner
    setEditingPartner(
      selectedPartner?.deliveryPartnerId == null ? false : true
    );

    setFormTitle(
      selectedPartner?.deliveryPartnerId == null
        ? I18n.t("APP_DELIVERY_PARTNERS_ADD_FORM_TITLE")
        : I18n.t("APP_DELIVERY_PARTNERS_UPDATE_FORM_TITLE")
    );

    // If editing a partner, override use props values of existing row
    if (
      selectedPartner?.deliveryPartnerId != null &&
      deliveryPartnerForm.deliveryPartnerId == 0
    ) {
      const form = selectedPartner;
      setForm(form);
    }
  }, [selectedPartner]);

  function getArrayPos() {
    // Get Array Pos of Delivery Partner to be edited, since ids and names can be both edited
    // Note -- not using a filtered by deliveryPartnerId, going off props data
    return deliveryPartners.findIndex(
      (x) => x.deliveryPartnerId === selectedPartner?.deliveryPartnerId
    );
  }

  async function handleChangeInput(
    event: { target: { name: any; value: any } } | null
  ) {
    if (event == null) return;

    let name = event.target.name;
    let value = event.target.value;

    const currentForm = deliveryPartnerForm;

    const _form = {
      ...currentForm,
      [name]: value,
    };
    setForm(_form);
  }

  function submitDisabled() {
    let bool: boolean;

    let invalidForm = isInvalidForm(
      deliveryPartnerForm,
      INPUT_VALIDATION_TYPES_DP
    );
    bool = invalidForm.isInvalid;

    const {
      deliveryPartnerId,
      deliveryPartnerName,
      grossSalesTransactionCode,
      commissionTransactionCode,
      salesWeekStartDay,
      salesWeekEndDay,
      paymentDayOfWeek,
    } = deliveryPartnerForm;

    if (
      //@ts-ignore
      deliveryPartnerId === "" ||
      deliveryPartnerName === "" ||
      //@ts-ignore
      grossSalesTransactionCode === "" ||
      //@ts-ignore
      commissionTransactionCode === "" ||
      salesWeekStartDay === "" ||
      salesWeekEndDay === "" ||
      paymentDayOfWeek === ""
    ) {
      bool = true;
    }

    return bool;
  }

  // Must add one commission rate history entry before submitting new delivery partner
  function goToCommissionRateForm() {
    let currentForm = { ...deliveryPartnerForm };

    if (currentForm["createdBy"] === "")
      currentForm["createdBy"] = oktaUserInfo[2];
    if (currentForm["lastUpdatedBy"] === "")
      currentForm["lastUpdatedBy"] = oktaUserInfo[2];

    // Automatically set End Day to 6 days after start day
    // @ts-ignore
    const endDay = getEndDay(deliveryPartnerForm.salesWeekStartDay);
    currentForm = {
      ...currentForm,
      salesWeekEndDay: endDay,
    };

    if (editingPartner) {
      // @ts-ignore
      dispatch(updateDeliveryPartners([currentForm]));
    } else {
      closeDeliveryPartnerModal();
      setDeliveryPartnerForm(currentForm);
      openCommissionRateForm(true);
    }
  }

  return (
    <>
      <Col className="DeliveryPartnerForm container">
        {!businessUnitOverridesForPartner.message ? (
          <>
            <Row>
              <Col className="container">
                <Card className="dashboard-card">
                  <CardBody>
                    <Row>
                      <FormGroup className="update-partner-form">
                        <Row className="close-button-container">
                          <img
                            alt=""
                            src={closeOutline}
                            className="close-modal-icon"
                            onClick={closeDeliveryPartnerModal}
                          />
                        </Row>
                        <Row className="container">
                          <Col className="update-partner-title-container">
                            <img
                              alt=""
                              src={deliveryCar}
                              className="update-partner-title-icon"
                            />
                            <h2 className="update-partner-title">
                              {formTitle}
                            </h2>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm="6" xs="12">
                            {/* PartnerID Input form if it needs to be displayed */}
                            {/* Partner ID */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_DELIVERY_PARTNERS_UPDATE_FORM_PARTNER_ID_LABEL"
                                )}
                              </Label>
                              <InputGroup>
                                <Input
                                  placeholder={I18n.t(
                                    "APP_DELIVERY_PARTNERS_UPDATE_FORM_PARTNER_ID_PLACEHOLDER"
                                  )}
                                  id="deliveryPartnerId"
                                  name="deliveryPartnerId"
                                  value={deliveryPartnerForm.deliveryPartnerId}
                                  //invalid={isInvalidInput(state.form.deliveryPartnerId, 'digits')}
                                  type="text"
                                  maxLength={55}
                                  // Use to disable id on edit
                                  disabled={editingPartner}
                                  onChange={handleChangeInput}
                                />
                                {/* TODO: change all to I18n */}
                                <FormFeedback>
                                  Please enter digits only
                                </FormFeedback>
                              </InputGroup>
                            </Row>

                            {/* Vendor Name */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_DELIVERY_PARTNERS_UPDATE_FORM_VENDOR_NAME_LABEL"
                                )}
                              </Label>
                              <InputGroup>
                                <Input
                                  placeholder={I18n.t(
                                    "APP_DELIVERY_PARTNERS_UPDATE_FORM_VENDOR_NAME_PLACEHOLDER"
                                  )}
                                  id="deliveryPartnerName"
                                  name="deliveryPartnerName"
                                  value={
                                    deliveryPartnerForm.deliveryPartnerName
                                  }
                                  type="text"
                                  maxLength={55}
                                  onChange={handleChangeInput}
                                />
                              </InputGroup>
                            </Row>

                            {/* Gross Sales Code Rate */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_DELIVERY_PARTNERS_UPDATE_FORM_GROSS_SALES_LABEL"
                                )}
                              </Label>
                              <InputGroup>
                                <Input
                                  placeholder={I18n.t(
                                    "APP_DELIVERY_PARTNERS_UPDATE_FORM_GROSS_SALES_PLACEHOLDER"
                                  )}
                                  id="grossSalesTransactionCode"
                                  name="grossSalesTransactionCode"
                                  //@ts-ignore
                                  value={
                                    deliveryPartnerForm.grossSalesTransactionCode
                                  }
                                  //invalid={isInvalidInput(this.state.form.grossSalesTransactionCode, 'digits')}
                                  type="text"
                                  maxLength={55}
                                  onChange={handleChangeInput}
                                />
                                <FormFeedback>
                                  Please enter digits only
                                </FormFeedback>
                              </InputGroup>
                            </Row>
                          </Col>
                          <Col sm="6" xs="12">
                            {/*  Transaction Code */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_DELIVERY_PARTNERS_UPDATE_FORM_COMMISSION_CODE_LABEL"
                                )}
                              </Label>
                              <InputGroup>
                                <Input
                                  placeholder={I18n.t(
                                    "APP_DELIVERY_PARTNERS_UPDATE_FORM_COMMISSION_PLACEHOLDER"
                                  )}
                                  id="commissionTransactionCode"
                                  name="commissionTransactionCode"
                                  //@ts-ignore
                                  value={
                                    deliveryPartnerForm.commissionTransactionCode
                                  }
                                  //invalid={isInvalidInput(this.state.form.commissionTransactionCode, 'digits')}
                                  type="text"
                                  maxLength={55}
                                  onChange={handleChangeInput}
                                />
                                <FormFeedback>
                                  Please enter digits only
                                </FormFeedback>
                              </InputGroup>
                            </Row>

                            {/* Sales Week Start Day */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_DELIVERY_PARTNERS_UPDATE_FORM_START_DAY_LABEL"
                                )}
                              </Label>
                              <InputGroup>
                                <Input
                                  type="select"
                                  id="salesWeekStartDay"
                                  name="salesWeekStartDay"
                                  value={deliveryPartnerForm.salesWeekStartDay}
                                  onChange={handleChangeInput}
                                >
                                  <option value="Monday">Monday</option>
                                  <option value="Tuesday">Tuesday</option>
                                  <option value="Wednesday">Wednesday</option>
                                  <option value="Thursday">Thursday</option>
                                  <option value="Friday">Friday</option>
                                  <option value="Saturday">Saturday</option>
                                  <option value="Sunday">Sunday</option>
                                </Input>
                              </InputGroup>
                            </Row>

                            {/* Payment Day of Week */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_DELIVERY_PARTNERS_UPDATE_FORM_PAYMENT_DAY_LABEL"
                                )}
                              </Label>
                              <InputGroup>
                                <Input
                                  type="select"
                                  id="paymentDayOfWeek"
                                  name="paymentDayOfWeek"
                                  value={deliveryPartnerForm.paymentDayOfWeek}
                                  onChange={handleChangeInput}
                                >
                                  <option value="Monday">Monday</option>
                                  <option value="Tuesday">Tuesday</option>
                                  <option value="Wednesday">Wednesday</option>
                                  <option value="Thursday">Thursday</option>
                                  <option value="Friday">Friday</option>
                                  <option value="Saturday">Saturday</option>
                                  <option value="Sunday">Sunday</option>
                                </Input>
                              </InputGroup>
                            </Row>
                          </Col>
                        </Row>
                      </FormGroup>
                    </Row>
                    <>
                      {error && (
                        <Row>
                          <h4 className="error">
                            {I18n.t("APP_DELIVERY_PARTNERS_UPDATE_FORM_ERROR")}
                          </h4>
                          {/* <h4 className="error">{JSON.stringify(this.state.error)}</h4> */}
                        </Row>
                      )}
                      <CardFooter>
                        {!loading ? (
                          <Button
                            disabled={submitDisabled()}
                            className="submit-button"
                            block
                            onClick={goToCommissionRateForm}
                          >
                            {editingPartner
                              ? I18n.t(
                                  "APP_DELIVERY_PARTNERS_UPDATE_FORM_SUBMIT"
                                )
                              : I18n.t(
                                  "APP_DELIVERY_PARTNERS_UPDATE_FORM_NEXT"
                                )}
                          </Button>
                        ) : (
                          <Row className="spinner-container">
                            <CFASpinner />
                          </Row>
                        )}
                      </CardFooter>
                    </>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        ) : (
          <Row>
            <Col className="success-container">
              <img alt="" src={successIcon} className="success-icon" />
              <h4>{I18n.t("APP_DELIVERY_PARTNERS_UPDATE_FORM_SUCCESS")}</h4>
              <Button
                className="go-back-button"
                block
                onClick={closeDeliveryPartnerModal}
              >
                {I18n.t("APP_DELIVERY_PARTNERS_UPDATE_FORM_CLOSE")}
              </Button>
            </Col>
          </Row>
        )}
      </Col>
    </>
  );
};

export default DeliveryPartnersForm;
